import { ajaxJson, ajaxFD } from 'imports/ajax';
import { createTypes } from 'redux-create-types';
import {
    map2programObj,
    map2Attachment,
    map2UserObj,
} from 'imports/mapper';
import { userActionCreators } from 'db/user/user.action';
import { attachmentActionsCreators } from 'db/attachment/attachment.action';
import { programActionCreators } from 'db/program/program.action';
import * as axios from 'axios';
import { createSelector } from 'redux-orm';
import { orm, dbStateSelector } from 'db';
import { userAttachmentsFnMaker } from './UploadDocumentSelectors';
import * as auth from 'imports/auth';


const namespace = 'page-enrollment-upload';

export const enrollmentUploadActions = createTypes([
    'toggleSpinner',
    'setError',
    'setUploadDocumentCancelToken',
], namespace);

function showSpinner(apiName) {
    return dispatch => {
        dispatch({
            type: enrollmentUploadActions.toggleSpinner,
            payload: true,
            meta: {apiName}
        });
    }
}

function hideSpinner(apiName) {
    return dispatch => {
        dispatch({
            type: enrollmentUploadActions.toggleSpinner,
            payload: false,
            meta: {apiName}
        });
    }
}

function getAttachmentStatusApiSuccess(res) {
    return dispatch => {
        const attachmentObjects = res.data.map(attachment => {
            return map2Attachment(attachment);
        });
        dispatch(userActionCreators.setAttachments(attachmentObjects));
        dispatch(hideSpinner('attachment-status'));
    }
}

function getAttachmentStatusApiFail(err) {
    return dispatch => {
        dispatch({
            type: enrollmentUploadActions.setError,
            payload: err,
        })
        return dispatch(hideSpinner('attachment-status'));
    }
}

function getAttachmentStatusFromApi() {
    return dispatch => {
        dispatch(showSpinner('attachment-status'));
        return ajaxJson.get('/users/attachments/status')
        .then(
            res => dispatch(getAttachmentStatusApiSuccess(res)),
            err => dispatch(getAttachmentStatusApiFail(err)),
        )
    }
}

function getProgramApiFail(err) {
    return dispatch => {
        dispatch({
            type: enrollmentUploadActions.setError,
            payload: err,
        })
        return dispatch(hideSpinner('program'));
    }
}

function getProgramApiSuccess(res) {
    return (dispatch, getState) => {
        const state = getState();
        const programObj = map2programObj(res.data);
        let newProgramObj = Object.assign({}, programObj);
        const userAttachments = createSelector(orm, dbStateSelector, userAttachmentsFnMaker())(state);
        const newAttachments = programObj.attachments
        .map(a => (a.id))
        .map(requiredAttachmentId => {
            const attachment = userAttachments.find((a) => a.id === requiredAttachmentId);
            if (!attachment) {
                throw(new Error(`there is not any attachment with id '${requiredAttachmentId}' in '/users/attachments/status'`));
            }
            return attachment;
        });
        newProgramObj = Object.assign(newProgramObj, {attachments: newAttachments});
        dispatch(programActionCreators.save(newProgramObj));
    }
}

function getProgramFromApi(programId) {
    return dispatch => {
        dispatch(showSpinner('program'));
        return ajaxJson.get(`/programs/${programId}`)
        .then(
            res => dispatch(getProgramApiSuccess(res)),
            err => dispatch(getProgramApiFail(err)),
        ).then(() => {
            return dispatch(hideSpinner('program'));
        })
    }
}

function makeUserLogin(user, token) {
    return dispatch => {
        auth.login({
            token,
            ...user
        });
    }
}

function getUserApiSuccess(res) {
    return dispatch => {
        const userObj = map2UserObj(res.data);
        dispatch(userActionCreators.set(userObj));
        dispatch(makeUserLogin(userObj, res.data['access_token']))
        dispatch(hideSpinner('user'));
    }
}

function getUserApiFail(err) {
    return dispatch => {
        dispatch({
            type: enrollmentUploadActions.setError,
            payload: err,
        })
        return dispatch(hideSpinner('user'));
    }
}

function getUserFromApi() {
    return dispatch => {
        dispatch(showSpinner('user'));
        return ajaxJson.get('/users')
        .then(
            res => dispatch(getUserApiSuccess(res)),    
            err => dispatch(getUserApiFail(err)),    
        )
    }
}

function initProgramAttachment(programId) {
    return dispatch => {
        return dispatch(getUserFromApi()).then(() => {
            return dispatch(getAttachmentStatusFromApi()).then(() => {
                return dispatch(getProgramFromApi(programId));
            })
        });
    }
}

function uploadDocumentApiSuccess(res, id) {
    return dispatch => {
        const dispatch1 = dispatch(attachmentActionsCreators.setFilePath(id, res.data["file"]));
        const dispatch2 = dispatch(attachmentActionsCreators.cleanUploadingProgress(id));
        const dispatch3 = dispatch(attachmentActionsCreators.setStatusUploaded(id));  
        return Promise.all([dispatch1, dispatch2, dispatch3]);
    }
}

function uploadDocumentApiFail(err, id) {
    return dispatch => {
        const dispatch1 = dispatch(attachmentActionsCreators.cleanUploadingProgress(id));
        const dispatch2 = dispatch(attachmentActionsCreators.setStatusError(id));
        dispatch({
            type: enrollmentUploadActions.setError,
            payload: err,
        });
        
        return Promise.all([dispatch1, dispatch2]).then(() => {
            return dispatch(hideSpinner('upload-document'));
        })
    }
}

function updateUploadingDocumentProgress(progressEvent, id) {
    return dispatch => {
        const progress = Math.trunc((progressEvent.loaded / progressEvent.total) * 100);
        dispatch(attachmentActionsCreators.setUploadingProgress(id, progress));
    }
}

function uploadDocumentToApi(formData, id) {
    return dispatch => {
        const cancelTokenSource = axios.CancelToken.source();        
        dispatch({
            type: enrollmentUploadActions.setUploadDocumentCancelToken,
            payload: {
                cancelTokenSource,
                id,
            }
        })
        const ajaxConfig = {
            onUploadProgress: (progressEvent) => {
                dispatch(updateUploadingDocumentProgress(progressEvent, id));
            },
            cancelToken: cancelTokenSource.token,
        };
        dispatch(attachmentActionsCreators.setStatusUploading(id));
        return ajaxFD.post(`/users/attachments`, formData, ajaxConfig)
        .then(
            res => dispatch(uploadDocumentApiSuccess(res, id)),
            err => dispatch(uploadDocumentApiFail(err, id)),
        )
    }
}

function uploadDocument(id, file) {
    return dispatch => {
        dispatch(showSpinner('upload-document'));
        const formData = new FormData();
        formData.append('attachment_title_uid', id);
        formData.append('file', file);
        dispatch(uploadDocumentToApi(formData, id, file));
    }
}

function cancelUploading(id) {
    return (dispatch, getState) => {
        const cancelTokens = getState().ui.enrollmentUploadDocumentPage.cancelTokens[id]
        if (cancelTokens) {
            cancelTokens.cancel('انصراف از آپلود.');
        }
    }
}

export const enrollmentUploadActionsCreators = {
    uploadDocument,
    initProgramAttachment,
    cancelUploading
}